import { initRum, isClientSide } from '@socar-inc/utils'
import { DefaultHeader } from '@src/shared/components/DefaultHeader'
import { RUN_MODE } from '@src/shared/constants/env'
import { initInterfaceBetweenSocarAppAndSocarWebBridge } from '@src/shared/utils/initInterfaceBetweenSocarAppAndSocarWebBridge'
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { NextPage } from 'next'
import { type AppProps } from 'next/app'
import { ReactElement, ReactNode, useState } from 'react'
import { RecoilRoot } from 'recoil'
import * as packageJSON from '../../package.json'
import '../shared/styles/global.css'

const { name: serviceName, version } = packageJSON

initRum({
  config: {
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID ?? '',
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
    env: RUN_MODE,
    service: `${RUN_MODE}/${serviceName}`,
    version,
  },
  enabled: process.env.NODE_ENV !== 'development',
})

if (isClientSide) {
  initInterfaceBetweenSocarAppAndSocarWebBridge()
}

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: false },
          mutations: { retry: false },
        },
      })
  )

  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={pageProps.dehydratedState}>
        <RecoilRoot>
          <DefaultHeader
            title={pageProps.title}
            allowZoom={pageProps.allowZoom}
          />
          {getLayout(<Component {...pageProps} />)}
          <ReactQueryDevtools
            initialIsOpen={process.env.NODE_ENV === 'development'}
          />
        </RecoilRoot>
      </HydrationBoundary>
    </QueryClientProvider>
  )
}

export default MyApp
